import { inject } from '@angular/core';
import { patchState, withMethods } from '@ngrx/signals';
import { DocumentsService } from '@data-access/documents/services';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { openFileInNewTab, saveAs } from '@shared/utils/download.util';
import { HttpParams } from '@angular/common/http';

export function documentFeatureMethods() {
  return withMethods((store, _documentService = inject(DocumentsService)) => {
    const _openDocumentInNewTab = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { error: null })),
        switchMap((documentId) =>
          _documentService.downloadDocument(documentId).pipe(
            tapResponse({
              error: (error: Error) => patchState(store, { error }),
              next: ({ blob }) => openFileInNewTab(blob),
            }),
          ),
        ),
      ),
    );
    const _downloadDocument = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { error: null })),
        switchMap((documentId) =>
          _documentService.downloadDocument(documentId).pipe(
            tapResponse({
              error: (error: Error) => patchState(store, { error }),
              next: ({ blob, filename }) => {
                saveAs(blob, filename);
              },
            }),
          ),
        ),
      ),
    );

    const _deleteDocument = rxMethod<{
      workspaceId: string;
      currentStage: string;
      documentId: string;
      onSuccess?: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { error: null, loading: true })),
        switchMap(({ workspaceId,currentStage, documentId, onSuccess }) =>
          _documentService
            .deleteDocument(workspaceId, currentStage, documentId)
            .pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, loading: false }),
                next: () => {
                  patchState(store, { loading: false });
                  onSuccess?.();
                },
              }),
            ),
        ),
      ),
    );

    const _bulkDeleteDocument = rxMethod<{
      workspaceId: string;
      documentIds: string;
      currentStage: string;
      onSuccess?: () => void;
    }>(
      pipe(
        tap(() => patchState(store, { error: null, loading: true })),
        switchMap(({ workspaceId, currentStage, documentIds, onSuccess }) => {
          const params = new HttpParams().set('document_ids', documentIds);
          return _documentService
            .bulkDeleteDocument(workspaceId, currentStage, params)
            .pipe(
              tapResponse({
                next: () => {
                  patchState(store, { loading: false });
                  onSuccess?.();
                },
                error: (error: Error) => {
                  patchState(store, { error, loading: false });
                },
              }),
            );
        }),
      ),
    );

    return {
      deleteDocument: (
        workspaceId: string,
        currentStage: string,
        documentId: string,
        onSuccess?: () => void,
      ) =>
        _deleteDocument({ workspaceId, currentStage, documentId, onSuccess }),
      bulkDeleteDocument: (
        workspaceId: string,
        currentStage: string,
        documentIds: string,
        onSuccess?: () => void,
      ) =>
        _bulkDeleteDocument({
          workspaceId,
          currentStage,
          documentIds,
          onSuccess,
        }),
      downloadDocument: (documentId: string) => _downloadDocument(documentId),
      openInNewTab: (documentId: string) => _openDocumentInNewTab(documentId),
    };
  });
}
/*
 * Common Document functions
 */
